import React, { useState } from 'react';
import Modal from 'components/common/Modal';
import { Loader2, AlertTriangle, CheckCircle, Package } from 'lucide-react';
import api from 'services/api';
import { formatarMoeda } from '../../utils';

const ModalCriarLote = ({ isOpen, onClose, contasSelecionadas, onLoteCriado }) => {
  const [saving, setSaving] = useState(false);
  const [nome, setNome] = useState('');
  const [error, setError] = useState(null);
  const NOME_MAX_LENGTH = 80;

  // Calcular total das contas selecionadas
  const totalValor = contasSelecionadas.reduce((sum, conta) => {
    return sum + (parseFloat(conta.valor) || 0);
  }, 0);

  const getVencimentoLabel = (conta) => {
    const base = conta?.data_vencimento_convertida || conta?.dataVencimento || conta?.data_vencimento;
    if (!base) return 'Sem data';
    const parsed = base instanceof Date ? base : new Date(base);
    if (Number.isNaN(parsed.getTime())) return 'Data inválida';
    return parsed.toLocaleDateString('pt-BR');
  };

  const handleCriarLote = async () => {
    if (contasSelecionadas.length === 0) {
      setError('Selecione pelo menos uma conta para criar o lote');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaPagarIds = contasSelecionadas.map(conta => conta.id);

      const response = await api.post('/n8n/pagamento-lotes', {
        conta_pagar_ids: contaPagarIds,
        nome: nome || null,
      });

      if (response.data.success) {
        if (onLoteCriado) {
          onLoteCriado(response.data.lote);
        }
        onClose();
        // Limpar seleção após criar
        setNome('');
      } else {
        setError(response.data.message || 'Erro ao criar lote');
      }
    } catch (err) {
      console.error('Erro ao criar lote:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao criar lote');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setNome('');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Criar Lote de Pagamentos"
      size="lg"
    >
      <div className="space-y-5 sm:space-y-6">
        {/* Aviso importante */}
        <div className="rounded-lg border border-[color:var(--status-warn,var(--border))]/50 bg-[color:var(--surface-muted)] p-4">
          <div className="flex items-start">
            <AlertTriangle className="mt-0.5 mr-3 h-5 w-5 flex-shrink-0 text-[color:var(--status-warn,var(--text-primary))]" />
            <div>
              <p className="text-sm font-semibold text-[color:var(--text-primary)]">
                Primeira autorização automática
              </p>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">
                Ao criar o lote, todas as contas serão automaticamente autorizadas por você (1ª autorização). 
                Um segundo usuário precisará autorizar para processar os pagamentos.
              </p>
            </div>
          </div>
        </div>

        {/* Nome do lote (opcional) */}
        <div>
          <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
            Nome do Lote (opcional)
          </label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Pagamentos Janeiro 2025"
            maxLength={NOME_MAX_LENGTH}
            className="w-full min-h-[44px] px-3 py-2 border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent"
            disabled={saving}
          />
          <div className="mt-1 flex items-center justify-end text-[11px] text-[color:var(--text-muted)]">
            {nome.length}/{NOME_MAX_LENGTH}
          </div>
        </div>

        {/* Resumo das contas selecionadas */}
        <div className="bg-[color:var(--surface-muted)] rounded-lg p-4 border border-[color:var(--border)]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[color:var(--text-primary)] flex items-center">
              <Package className="h-4 w-4 mr-2" />
              Contas Selecionadas
            </h4>
            <span className="text-sm text-[color:var(--text-muted)]">
              {contasSelecionadas.length} {contasSelecionadas.length === 1 ? 'conta' : 'contas'}
            </span>
          </div>

          <div className="space-y-2 max-h-[34vh] sm:max-h-60 overflow-y-auto pr-1">
            {contasSelecionadas.map((conta) => (
              <div
                key={conta.id}
                className="bg-[color:var(--surface)] rounded-md p-3 border border-[color:var(--border)] flex items-center justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-[color:var(--text-muted)]">#{conta.id}</span>
                    <span className="text-sm font-medium text-[color:var(--text-primary)] truncate">
                      {conta.fornecedor || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-[color:var(--text-muted)] truncate">
                    {conta.observacao || conta.descricao || 'Sem descrição'}
                  </p>
                  <p className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                    Vencimento: <span className="text-[color:var(--text-primary)]">{getVencimentoLabel(conta)}</span>
                  </p>
                </div>
                <div className="ml-4 text-right">
                  <p className="text-sm font-semibold text-[color:var(--text-primary)]">
                    {formatarMoeda(parseFloat(conta.valor) || 0)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex items-center justify-between">
            <span className="text-sm font-semibold text-[color:var(--text-primary)]">Total:</span>
            <span className="text-lg font-bold text-[color:var(--accent)]">
              {formatarMoeda(totalValor)}
            </span>
          </div>
        </div>

        {/* Erro */}
        {error && (
          <div role="alert" className="rounded-lg border border-[color:var(--status-bad,var(--border))]/50 bg-[color:var(--surface-muted)] p-4">
            <div className="flex items-start">
              <AlertTriangle className="mt-0.5 mr-3 h-5 w-5 flex-shrink-0 text-[color:var(--status-bad,var(--accent))]" />
              <p className="text-sm text-[color:var(--status-bad,var(--text-primary))]">{error}</p>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="sticky bottom-0 z-10 -mx-1 bg-[color:var(--surface)]/95 backdrop-blur-sm px-1 pt-3 border-t border-[color:var(--border)]">
          <div className="mb-2 flex items-center justify-between text-xs text-[color:var(--text-muted)]">
            <span>{contasSelecionadas.length} {contasSelecionadas.length === 1 ? 'conta selecionada' : 'contas selecionadas'}</span>
            <span className="font-semibold text-[color:var(--text-primary)]">{formatarMoeda(totalValor)}</span>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <button
            onClick={handleClose}
            disabled={saving}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleCriarLote}
            disabled={saving || contasSelecionadas.length === 0}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] border border-transparent rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--accent)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                Criando...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Criar lote ({contasSelecionadas.length})
              </>
            )}
          </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalCriarLote;




